import { JsonObject, JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { IConfig } from '../../../../Assembler/dist/config/iConfig.js';
import { DynamicConfigMap, mergeDynamicConfig, parseDynamicConfig } from '../../../../Assembler/dist/registry/dynamicConfigMap.js';
import type { LoaderPreProcessJson } from './loaderPreProcessJson.js';
import { Logger } from '../../../../Arshu/dist/logger/logger.js';


export const lifecycleMethods = {
    load(this: LoaderPreProcessJson, config: IConfig, appSite: string): boolean {
        // Incremental, accumulating load (mirrors csharp LoaderPreProcessJson.Load and node's
        // LoaderNormalJson.load): per-AppSite store setters and the dynamic-config preload ADD to the
        // existing stores. NO clearCache() here — clearing on every load wiped a prior AppSite's
        // dynamic-AppSite config when a redirect target loaded via ensureAppSiteLoaded() during a merge
        // (the JsonRule83ADynAppSite -> JsonRule80ADynData case), leaving the round-trip unable to replay
        // the redirect. Per-scenario isolation is provided by a fresh loader instance, not by clearing here.
        return this._loadImpl(config, appSite);
    },

    ensureAppSiteLoaded(this: LoaderPreProcessJson, appSite: string): boolean {
        if (!appSite || !this._config) {
            return false;
        }
        return this.load(this._config, appSite);
    },

    clearCache(this: LoaderPreProcessJson): void {
        this._store.clear();
        this._sourceJsonCache.clear();
        this._dynamicDataConfigStore = {};
        this._dynamicClientDataConfigStore = {};
        this._dynamicViewConfigStore = {};
        this._dynamicActionConfigStore = {};
        this._dynamicAppSiteConfigStore = {};
        this._dynamicDataParamsStore = new JsonObject();
        this._dynamicClientDataParamsStore = new JsonObject();
        this._dynamicViewParamsStore = new JsonObject();
        this._dynamicActionParamsStore = new JsonObject();
        this._dynamicAppSiteParamsStore = new JsonObject();
        this._globalDataStore = {};
        this._globalViewDataStore = {};
        this._globalViewDataComponentStore = {};
    },

    _preloadDynamicAndGlobalData(this: LoaderPreProcessJson, config: IConfig, appSite: string): void {
        Logger.flow(`event=preloadData phase=PerScenario engine=PreProcessJson appSite=${appSite}`, "Lifecycle");
        const allAppSites = [appSite];
        const searchAppSites = config.getSearchAppSitesForAppSite(appSite);
        if (searchAppSites) {
            for (const s of searchAppSites.split(',')) {
                const trimmed = s.trim();
                if (trimmed && !allAppSites.includes(trimmed)) {
                    allAppSites.push(trimmed);
                }
            }
        }

        for (const site of allAppSites) {
            const ddFolderJson = config.getFolderDataJson(site, 'dynamicdata');
            const ddAppSiteJson = config.getDataJson(site, 'dynamicdata');
            let ddConfig: DynamicConfigMap | null = null;
            const ddFolderConfig = parseDynamicConfig(ddFolderJson, 'Data');
            const ddAppSiteConfig = parseDynamicConfig(ddAppSiteJson, 'Data');
            if (ddFolderConfig) ddConfig = ddFolderConfig;
            if (ddAppSiteConfig) {
                if (!ddConfig) ddConfig = ddAppSiteConfig;
                else mergeDynamicConfig(ddConfig, ddAppSiteConfig);
            }
            this._dynamicDataConfigStore[site] = ddConfig;

            if (ddConfig) {
                for (const [, handlers] of ddConfig) {
                    if (!Array.isArray(handlers)) continue;
                    for (const handler of handlers) {
                        const paramFile = handler.iParamFile;
                        if (!paramFile) continue;
                        const storeKey = `${site}:${paramFile}`;
                        if (this._dynamicDataParamsStore.containsKey(storeKey)) continue;
                        const fileName = paramFile.replace(/DynamicData\./i, 'dynamicdata.');
                        const paramJson = config.getDataJson(site, fileName) || config.getFolderDataJson(site, fileName);
                        if (paramJson) this._dynamicDataParamsStore.set(storeKey, JsonValue.object(paramJson));
                    }
                }
            }

            // CLIENT-DATA preparer bindings (dynamicclientdata.json) — parsed like dynamicdata.json but kept
            // in a SEPARATE store so client-data handlers never mix into the server IData path. "Data" legacy
            // key fallback is harmless here (bindings use unified IType/IParamFile + the DataSource set).
            const cdFolderJson = config.getFolderDataJson(site, 'dynamicclientdata');
            const cdAppSiteJson = config.getDataJson(site, 'dynamicclientdata');
            let cdConfig: DynamicConfigMap | null = null;
            const cdFolderConfig = parseDynamicConfig(cdFolderJson, 'Data');
            const cdAppSiteConfig = parseDynamicConfig(cdAppSiteJson, 'Data');
            if (cdFolderConfig) cdConfig = cdFolderConfig;
            if (cdAppSiteConfig) {
                if (!cdConfig) cdConfig = cdAppSiteConfig;
                else mergeDynamicConfig(cdConfig, cdAppSiteConfig);
            }
            this._dynamicClientDataConfigStore[site] = cdConfig;

            if (cdConfig) {
                for (const [, handlers] of cdConfig) {
                    if (!Array.isArray(handlers)) continue;
                    for (const handler of handlers) {
                        const paramFile = handler.iParamFile;
                        if (!paramFile) continue;
                        const storeKey = `${site}:${paramFile}`;
                        if (this._dynamicClientDataParamsStore.containsKey(storeKey)) continue;
                        const fileName = paramFile.replace(/DynamicClientData\./i, 'dynamicclientdata.');
                        const paramJson = config.getDataJson(site, fileName) || config.getFolderDataJson(site, fileName);
                        if (paramJson) this._dynamicClientDataParamsStore.set(storeKey, JsonValue.object(paramJson));
                    }
                }
            }

            const dvFolderJson = config.getFolderDataJson(site, 'dynamicview');
            const dvAppSiteJson = config.getDataJson(site, 'dynamicview');
            let dvConfig: DynamicConfigMap | null = null;
            const dvFolderConfig = parseDynamicConfig(dvFolderJson, 'View');
            const dvAppSiteConfig = parseDynamicConfig(dvAppSiteJson, 'View');
            if (dvFolderConfig) dvConfig = dvFolderConfig;
            if (dvAppSiteConfig) {
                if (!dvConfig) dvConfig = dvAppSiteConfig;
                else mergeDynamicConfig(dvConfig, dvAppSiteConfig);
            }
            this._dynamicViewConfigStore[site] = dvConfig;

            if (dvConfig) {
                for (const [, handlers] of dvConfig) {
                    if (!Array.isArray(handlers)) continue;
                    for (const handler of handlers) {
                        const paramFile = handler.iParamFile;
                        if (!paramFile) continue;
                        const storeKey = `${site}:${paramFile}`;
                        if (this._dynamicViewParamsStore.containsKey(storeKey)) continue;
                        const fileName = paramFile.replace(/DynamicView\./i, 'dynamicview.');
                        const paramJson = config.getDataJson(site, fileName) || config.getFolderDataJson(site, fileName);
                        if (paramJson) this._dynamicViewParamsStore.set(storeKey, JsonValue.object(paramJson));
                    }
                }
            }

            const daFolderJson = config.getFolderDataJson(site, 'dynamicaction');
            const daAppSiteJson = config.getDataJson(site, 'dynamicaction');
            let daConfig: DynamicConfigMap | null = null;
            const daFolderConfig = parseDynamicConfig(daFolderJson, 'Action');
            const daAppSiteConfig = parseDynamicConfig(daAppSiteJson, 'Action');
            if (daFolderConfig) daConfig = daFolderConfig;
            if (daAppSiteConfig) {
                if (!daConfig) daConfig = daAppSiteConfig;
                else mergeDynamicConfig(daConfig, daAppSiteConfig);
            }
            this._dynamicActionConfigStore[site] = daConfig;

            if (daConfig) {
                for (const [, handlers] of daConfig) {
                    if (!Array.isArray(handlers)) continue;
                    for (const handler of handlers) {
                        const paramFile = handler.iParamFile;
                        if (!paramFile) continue;
                        const storeKey = `${site}:${paramFile}`;
                        if (this._dynamicActionParamsStore.containsKey(storeKey)) continue;
                        const fileName = paramFile.replace(/DynamicAction\./i, 'dynamicaction.');
                        const paramJson = config.getDataJson(site, fileName) || config.getFolderDataJson(site, fileName);
                        if (paramJson) this._dynamicActionParamsStore.set(storeKey, JsonValue.object(paramJson));
                    }
                }
            }

            // DynamicAppSite handlers (AppSite-triggered). Mirrors C# loader lifecycle: the wwwroot-level
            // wildcard ("*") layer (getRootJson) is least-specific; folder then appsite override it.
            const dsRootJson = config.getRootJson('dynamicappsite.json');
            const dsFolderJson = config.getFolderDataJson(site, 'dynamicappsite');
            const dsAppSiteJson = config.getDataJson(site, 'dynamicappsite');
            let dsConfig: DynamicConfigMap | null = null;
            const dsRootConfig = (dsRootJson && dsRootJson.size > 0) ? parseDynamicConfig(dsRootJson, 'AppSite') : null;
            const dsFolderConfig = parseDynamicConfig(dsFolderJson, 'AppSite');
            const dsAppSiteConfig = parseDynamicConfig(dsAppSiteJson, 'AppSite');
            if (dsRootConfig) dsConfig = dsRootConfig;
            if (dsFolderConfig) {
                if (!dsConfig) dsConfig = dsFolderConfig;
                else mergeDynamicConfig(dsConfig, dsFolderConfig);
            }
            if (dsAppSiteConfig) {
                if (!dsConfig) dsConfig = dsAppSiteConfig;
                else mergeDynamicConfig(dsConfig, dsAppSiteConfig);
            }
            this._dynamicAppSiteConfigStore[site] = dsConfig;

            if (dsConfig) {
                for (const [, handlers] of dsConfig) {
                    if (!Array.isArray(handlers)) continue;
                    for (const handler of handlers) {
                        const paramFile = handler.iParamFile;
                        if (!paramFile) continue;
                        const storeKey = `${site}:${paramFile}`;
                        if (this._dynamicAppSiteParamsStore.containsKey(storeKey)) continue;
                        const fileName = paramFile.replace(/DynamicAppSite\./i, 'dynamicappsite.');
                        let paramJson = config.getDataJson(site, fileName) || config.getFolderDataJson(site, fileName);
                        if (!paramJson) {
                            const rootParam = config.getRootJson(fileName + '.json');
                            if (rootParam && rootParam.size > 0) paramJson = rootParam;
                        }
                        if (paramJson) this._dynamicAppSiteParamsStore.set(storeKey, JsonValue.object(paramJson));
                    }
                }
            }

            this._globalDataStore[site] = config.getDataJson(site, 'globaldata');

            const gvd = config.getDataJson(site, 'globalviewdata');
            if (gvd) {
                const resolvedGvd = new JsonObject();
                const gvdKeys: string[] = gvd instanceof Map ? Array.from(gvd.keys()) : Object.keys(gvd);
                for (const componentName of gvdKeys) {
                    const componentStoreKey = `${site}:${componentName.toLowerCase()}`;
                    const rawMappingValue = gvd instanceof Map ? gvd.get(componentName) : (gvd as Record<string, unknown>)[componentName];
                    const mappingValue = rawMappingValue instanceof JsonValue ? (rawMappingValue.asString() ?? rawMappingValue.asObject() ?? rawMappingValue) : rawMappingValue;
                    let resolvedData: JsonObject | null = null;
                    if (typeof mappingValue === 'string' && mappingValue) {
                        resolvedData = config.getDataJson(site, mappingValue);
                    } else if (mappingValue instanceof JsonObject) {
                        resolvedData = mappingValue;
                    } else if (mappingValue && typeof mappingValue === 'object') {
                        resolvedData = JsonObject.fromPlainObject(mappingValue as Record<string, unknown>);
                    }
                    if (resolvedData) {
                        resolvedGvd.set(componentName, JsonValue.object(resolvedData));
                        if (this._globalViewDataComponentStore[componentStoreKey] === undefined) {
                            this._globalViewDataComponentStore[componentStoreKey] = resolvedData;
                        }
                    }
                }
                this._globalViewDataStore[site] = resolvedGvd.size > 0 ? resolvedGvd : null;
            } else {
                this._globalViewDataStore[site] = null;
            }
        }
    }
};
